import { ref, update } from 'firebase/database';
import { database } from '../config/firebase';
import { createLogger } from './logger';

// ログシステム初期化
const logger = createLogger('DragDrop');

/**
 * 生徒リストの並び替え処理
 * @param {Object} result - ドラッグ結果
 * @param {Array} students - 生徒リスト
 * @param {Object} currentUser - 現在のユーザー
 * @returns {Promise<boolean>} 処理実行の可否
 */
export const handleStudentListReorder = async (result, students, currentUser) => {
  const { destination, source } = result;
  
  if (!currentUser.isMaster) return false;

  // 現在の生徒リストをソート
  const sortedStudents = [...students].sort((a, b) => (a.order || 0) - (b.order || 0));

  // 並び替え処理
  const reorderedStudents = Array.from(sortedStudents);
  const [movedStudent] = reorderedStudents.splice(source.index, 1);
  reorderedStudents.splice(destination.index, 0, movedStudent);

  // 並び替え後のorderを設定
  const updatedStudents = reorderedStudents.map((student, index) => ({
    ...student,
    order: index,
  }));

  // Firebase に order を更新
  const updates = {};
  updatedStudents.forEach((student, index) => {
    updates[`students/${student.id}/order`] = index;
  });

  await update(ref(database), updates);
  
  logger.info('生徒リスト並び替え完了', {
    movedStudentId: movedStudent.id,
    fromIndex: source.index,
    toIndex: destination.index
  });
  
  return true;
};

/**
 * タスクの並び替え処理
 * @param {Object} result - ドラッグ結果
 * @param {Object} tasks - タスクデータ
 * @param {Function} getStudentId - 生徒ID取得関数
 * @param {Object} selectedSubject - 選択中の科目
 * @returns {Promise<boolean>} 処理実行の可否
 */
export const handleTaskReorder = async (result, tasks, getStudentId, selectedSubject) => {
  const { destination, source } = result;
  
  const studentId = getStudentId();
  if (!studentId) return false;
  
  const key = `${studentId}-${selectedSubject.id}`;

  const taskList = tasks[key]
    ? Object.values(tasks[key]).sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];

  const reorderedTasks = Array.from(taskList);
  const [movedTask] = reorderedTasks.splice(source.index, 1);
  reorderedTasks.splice(destination.index, 0, movedTask);

  const taskUpdates = {};
  reorderedTasks.forEach((task, index) => {
    taskUpdates[`tasks/${key}/${task.id}/order`] = index;
  });

  await update(ref(database), taskUpdates);
  
  logger.info('タスク並び替え完了', {
    movedTaskId: movedTask.id,
    fromIndex: source.index,
    toIndex: destination.index,
    key
  });
  
  return true;
};

/**
 * チェック項目の並び替え処理
 * @param {Object} result - ドラッグ結果
 * @param {Object} tasks - タスクデータ
 * @param {Function} getStudentId - 生徒ID取得関数
 * @param {Object} selectedSubject - 選択中の科目
 * @returns {Promise<boolean>} 処理実行の可否
 */
export const handleTaskItemReorder = async (result, tasks, getStudentId, selectedSubject) => {
  const { destination, source, type } = result;
  
  const taskId = type.replace('taskItems-', '');
  const studentId = getStudentId();
  if (!studentId) return false;
  
  const key = `${studentId}-${selectedSubject.id}`;
  const task = tasks[key]?.[taskId];
  if (!task) return false;

  const itemList = Object.entries(task.items || {}).sort(
    ([, a], [, b]) => (a.order || 0) - (b.order || 0)
  );

  const reorderedItems = Array.from(itemList);
  const [movedItem] = reorderedItems.splice(source.index, 1);
  reorderedItems.splice(destination.index, 0, movedItem);

  const itemUpdates = {};
  reorderedItems.forEach(([itemId], index) => {
    itemUpdates[`tasks/${key}/${taskId}/items/${itemId}/order`] = index;
  });

  await update(ref(database), itemUpdates);
  
  logger.info('チェック項目並び替え完了', {
    movedItemId: movedItem[0],
    taskId,
    fromIndex: source.index,
    toIndex: destination.index,
    key
  });
  
  return true;
};

/**
 * ドラッグ&ドロップ処理のメインハンドラー
 * @param {Object} result - ドラッグ結果
 * @param {Object} dependencies - 必要な依存関係
 * @returns {Promise<void>}
 */
export const handleDragEnd = async (result, dependencies) => {
  const { destination, source, type } = result;
  const { students, tasks, getStudentId, selectedSubject, currentUser } = dependencies;

  if (!destination) return;
  if (
    destination.droppableId === source.droppableId &&
    destination.index === source.index
  )
    return;

  try {
    // 生徒リストの並び替え
    if (
      source.droppableId.startsWith('studentList') &&
      destination.droppableId.startsWith('studentList')
    ) {
      await handleStudentListReorder(result, students, currentUser);
      return;
    }

    // タスクの並び替え
    if (type === 'task') {
      await handleTaskReorder(result, tasks, getStudentId, selectedSubject);
      return;
    }

    // チェック項目の並び替え
    if (type.startsWith('taskItems-')) {
      await handleTaskItemReorder(result, tasks, getStudentId, selectedSubject);
      return;
    }

  } catch (error) {
    logger.error('並び替えの更新に失敗しました', { 
      error: error.message,
      sourceId: source.droppableId,
      destinationId: destination.droppableId,
      type
    });
    alert('並び替えの更新に失敗しました。再度お試しください。');
  }
}; 